import type { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';

import { Alert, AlertVariants } from '../../Alert';
import { Box, Flex, HStack, type BoxProps } from '../../Core';
import { Text } from '../../Text';
import { Tooltip, type TooltipProps } from '../../Tooltip';
import { Label } from '../styles';
import { FormControlSizes } from '../types';
import { OverflowFormMessage } from './OverflowFormMessage';
import { FormGroupContentWrapper, FormGroupWrapper, HelpText } from './styles';

export const FormGroup = styled(
  ({
    label,
    labelSuffix,
    labelWidth,
    controlId,
    children,
    help,
    error,
    warning,
    disabled,
    required,
    size = FormControlSizes.Default,
    inline = false,
    style,
    tooltip: _tooltip,
    tooltipProps,
    portalizeTooltip,
    useErrorBorder,
    guideText,
    withMessageSpacing = false,
    ...props
  }: FormGroupProps) => {
    const { gap = 0 } = props;

    const renderTooltip = _tooltip === '' ? false : !!_tooltip;
    const tooltip = renderTooltip ? _tooltip : null;

    return (
      <FormGroupWrapper
        disabled={disabled}
        size={size}
        style={style}
        inline={inline}
        $withMessageSpacing={withMessageSpacing}
        data-testid="form-group"
        data-error={error ? 'true' : undefined}
        {...props}
      >
        {label && (
          <HStack
            justifyContent="space-between"
            mb={inline ? 0 : 'spacing4'}
            h={inline ? 0 : undefined}
            minHeight={inline ? 0 : 'spacing12'}
          >
            <Flex w={labelWidth}>
              <Label tooltip={renderTooltip} htmlFor={controlId} data-testid="form-group-label">
                <Tooltip {...tooltipProps} tooltip={tooltip} usePortal={portalizeTooltip}>
                  {label}
                  {required && '*'}
                </Tooltip>
              </Label>
            </Flex>
            {labelSuffix && (
              <Flex>
                <Label htmlFor={controlId}>{labelSuffix}</Label>
              </Flex>
            )}
          </HStack>
        )}
        {useErrorBorder ? (
          <FormGroupContentWrapper showErrorBorder={!!error} size={size}>
            {children}
          </FormGroupContentWrapper>
        ) : (
          children
        )}
        {(error || warning) && (
          <OverflowFormMessage
            data-testid="form-message-error"
            pl={gap}
            ml={inline ? labelWidth : undefined}
            error={!!error}
            warning={!!warning}
          >
            {error ? error : warning}
          </OverflowFormMessage>
        )}
        {help && !error && !warning && (
          <HelpText data-testid="form-group-help-text" ml={inline ? labelWidth : undefined}>
            {help}
          </HelpText>
        )}
        {guideText && (
          <Box mt={error || warning || help ? 'spacing16' : 'spacing6'} data-testid="form-group-guide-text">
            <Alert variant={AlertVariants.Guide} p="spacing8" dismissable={false}>
              {typeof guideText === 'string' ? <Text color="colorTextDefault">{guideText}</Text> : guideText}
            </Alert>
          </Box>
        )}
      </FormGroupWrapper>
    );
  }
)``;

export interface FormGroupProps extends BoxProps {
  labelWidth?: CSSProperties['width'];
  controlId?: string;
  disabled?: boolean;
  label?: ReactNode;
  /** When true, adds a * after the label to indicate the field is required */
  required?: boolean;
  labelSuffix?: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  warning?: ReactNode;
  tooltip?: ReactNode;
  /** Additional presentation and positioning options for the label tooltip. */
  tooltipProps?: Omit<TooltipProps, 'children' | 'tooltip' | 'usePortal'>;
  portalizeTooltip?: boolean;
  inline?: boolean;
  size?: FormControlSizes;
  children?: ReactNode;
  style?: CSSProperties;
  /**
   * If true, the FormGroup will wrap the erroring controls in an error border
   * (without it, we rely on the controls self-support of error display
   */
  useErrorBorder?: boolean;
  /** When provided, renders a guide Alert below the control, after error and help text. */
  guideText?: ReactNode;
  /** Places error, warning, and help text in the normal layout flow. */
  withMessageSpacing?: boolean;
}
